import React, { useRef, useCallback } from 'react';
import { Box, Typography } from '@mui/material';

interface PromptFieldProps {
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  inputRef?: React.Ref<any>;
  label: string;
  helperText?: string;
  accentColor: string;
  placeholder?: string;
  recommendation?: string;
  minRows?: number;
  readOnly?: boolean;
  error?: boolean;
  errorMessage?: string;
}

const PromptField: React.FC<PromptFieldProps> = ({
  value,
  onChange,
  onBlur,
  inputRef,
  label,
  helperText,
  accentColor,
  placeholder,
  recommendation,
  minRows = 10,
  readOnly = false,
  error = false,
  errorMessage,
}) => {
  const internalRef = useRef<HTMLTextAreaElement>(null);

  const setRefs = useCallback(
    (el: HTMLTextAreaElement | null) => {
      (internalRef as any).current = el;
      if (typeof inputRef === 'function') {
        inputRef(el);
      } else if (inputRef && typeof inputRef === 'object') {
        (inputRef as any).current = el;
      }
    },
    [inputRef]
  );

  const charCount = (value || '').length;

  return (
    <Box sx={{ width: '100%' }}>
      {/* Label row */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 0.5,
        }}
      >
        <Typography
          sx={{
            fontSize: '0.7rem',
            fontWeight: 700,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: 'text.secondary',
          }}
        >
          {label}
        </Typography>
        {readOnly ? (
          <Typography
            sx={{
              fontSize: '0.7rem',
              fontStyle: 'italic',
              color: 'text.disabled',
            }}
          >
            read-only
          </Typography>
        ) : (
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              px: 1,
              py: 0.25,
              borderRadius: '10px',
              border: `1px solid ${accentColor}40`,
              backgroundColor: `${accentColor}08`,
            }}
          >
            <Typography
              sx={{
                fontSize: '0.7rem',
                fontWeight: 600,
                color: accentColor,
                fontFamily: 'monospace',
              }}
            >
              {charCount} chars
            </Typography>
          </Box>
        )}
      </Box>

      {/* Helper text */}
      {helperText && (
        <Typography
          sx={{
            fontSize: '0.75rem',
            color: 'text.secondary',
            mb: 1,
            lineHeight: 1.4,
          }}
        >
          {helperText}
        </Typography>
      )}

      {/* Textarea */}
      <textarea
        ref={setRefs}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        placeholder={placeholder}
        readOnly={readOnly}
        rows={minRows}
        style={{
          width: '100%',
          fontFamily: '"SF Mono", "Fira Code", "Fira Mono", Menlo, Consolas, monospace',
          fontSize: '0.85rem',
          lineHeight: '1.6',
          padding: '12px 14px',
          borderRadius: '8px',
          border: `1px solid ${error ? '#d32f2f' : '#ccc'}`,
          outline: 'none',
          resize: 'vertical',
          backgroundColor: readOnly ? '#f5f5f5' : '#fff',
          color: readOnly ? '#666' : '#1a1a1a',
          boxSizing: 'border-box',
          transition: 'border-color 0.2s',
        }}
        onFocus={(e) => {
          if (!readOnly) {
            e.target.style.borderColor = accentColor;
            e.target.style.boxShadow = `0 0 0 2px ${accentColor}20`;
          }
        }}
        onBlurCapture={(e) => {
          e.target.style.borderColor = error ? '#d32f2f' : '#ccc';
          e.target.style.boxShadow = 'none';
        }}
      />

      {/* Error message */}
      {error && errorMessage && (
        <Typography sx={{ fontSize: '0.75rem', color: '#d32f2f', mt: 0.5 }}>
          {errorMessage}
        </Typography>
      )}

      {/* Recommendation */}
      {recommendation && !error && (
        <Typography
          sx={{
            fontSize: '0.7rem',
            color: 'text.disabled',
            mt: 0.5,
            fontStyle: 'italic',
          }}
        >
          {recommendation}
        </Typography>
      )}
    </Box>
  );
};

export default PromptField;
